/**
 * Anlegeformulare überleben Seitenwechsel und Neuladen — an EINER Stelle
 * gebaut, damit keine Seite es vergisst (HJ-025).
 *
 * ═══ WAS DER TESTLAUF GEZEIGT HAT ═══
 *
 * „Neue Tour" hatte schon einen Entwurf, aber das Formular selbst ging beim
 * Seitenwechsel ZU: Ob es offen war, wusste nur der Zustand der Seite, und
 * den baut React beim Verlassen ab. Wer zurückkam, sah die Liste und keinen
 * Hinweis — die Eingaben wirkten verloren, obwohl sie im Speicher lagen. Bei
 * Kunden, Orten, Fahrzeugen, Strecken und Mitarbeitern waren sie es wirklich.
 *
 * ═══ ZWEI TEILE ═══
 *
 * `useFormularOffen` merkt sich, DASS ein Formular offen ist — in
 * `sessionStorage` (überlebt Seitenwechsel über die Navigation und Neuladen)
 * und zusätzlich in der Adresse als `?neu=1` (Zurück-Knopf, Lesezeichen,
 * und man sieht, warum die Seite anders aussieht).
 *
 * `useEntwurfsfeld` merkt sich, WAS darin steht — ein Feld je Aufruf, damit
 * ein Formular nur seine `useState('')` austauscht und sonst gleich bleibt.
 *
 * Beide hängen am selben Schlüssel. Zugemacht wird nur über
 * `setzeOffen(false)` — und das räumt den Entwurf mit weg: „Abbrechen" heißt
 * verwerfen, und ein liegengebliebener Entwurf käme beim nächsten Öffnen
 * wieder hoch (siehe `daten/entwurf.ts`, warum das gefährlich ist).
 *
 * Warum `sessionStorage` und nicht `localStorage`: steht ausführlich in
 * `daten/entwurf.ts` — ein Entwurf darf nicht wochenlang altern.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  entwurfLesen,
  entwurfLoeschen,
  entwurfSchreiben,
  fluechtigLoeschen,
} from './entwurf';

const OFFEN = 'offen.';

export function useFormularOffen(
  schluessel: string,
): [boolean, (offen: boolean) => void] {
  const [adresse, setAdresse] = useSearchParams();
  const [offen, setOffen] = useState(
    () => adresse.get('neu') === '1' || entwurfLesen<boolean>(OFFEN + schluessel) === true,
  );

  // Kommt man über die Navigation zurück (ohne `?neu=1`), aber mit offenem
  // Formular im Speicher, wird der Zusatz nachgetragen — `replace`, damit der
  // Zurück-Knopf nicht erst durch einen Zwischenschritt muss. Ein Effekt,
  // weil die Adresse ein System außerhalb von React ist.
  //
  // NUR in diese Richtung. Das Entfernen geschieht in `setzeOffen`, also im
  // Ereignis: Ein Effekt, der auch entfernt, lief einmal NACH einem Sprung
  // zur Tour und schrieb die Adresse zurück auf `/disposition` — man landete
  // wieder in der Liste statt in der Tour.
  // ═══ NACH DEM ABBAU NICHT MEHR NAVIGIEREN ═══
  //
  // `setzeOffen(false)` kommt oft nach einem `await` („Anlegen" → Antwort →
  // `fertig()`). Hat die Person in der Zwischenzeit die Seite gewechselt,
  // ist dieses Formular schon abgebaut — und `setAdresse` navigierte mit der
  // Adresse von damals zurück: von „Orte" wieder auf „Kunden", per
  // `replace`, also auch aus dem Verlauf getilgt (Review HJ-025). Ist die
  // Seite weg, wird deshalb nur noch aufgeräumt, nicht mehr navigiert.
  const eingebaut = useRef(true);
  useEffect(() => {
    eingebaut.current = true;
    return () => {
      eingebaut.current = false;
    };
  }, []);

  useEffect(() => {
    if (!offen || adresse.get('neu') === '1') return;
    const neu = new URLSearchParams(adresse);
    neu.set('neu', '1');
    setAdresse(neu, { replace: true });
  }, [offen, adresse, setAdresse]);

  function setzeOffen(wert: boolean) {
    setOffen(wert);
    if (wert) {
      entwurfSchreiben(OFFEN + schluessel, true);
      return;
    }
    entwurfLoeschen(OFFEN + schluessel);
    entwurfLoeschen(schluessel);
    fluechtigLoeschen(schluessel);
    if (eingebaut.current && adresse.get('neu') === '1') {
      const neu = new URLSearchParams(adresse);
      neu.delete('neu');
      setAdresse(neu, { replace: true });
    }
  }

  return [offen, setzeOffen];
}

/**
 * Ein Feld eines Anlegeformulars mit Entwurf. Gleiche Form wie `useState`,
 * damit der Tausch in einem Formular eine Zeile kostet.
 *
 * Geschrieben wird beim Setzen, nicht in einem Effekt: Jede Eingabe ist das
 * Ereignis, und ein Effekt hinkte einen Rendervorgang hinterher.
 */
export function useEntwurfsfeld<T>(
  formular: string,
  feld: string,
  anfang: T,
): [T, (wert: T) => void] {
  const [wert, setWert] = useState<T>(() => {
    const entwurf = entwurfLesen<Record<string, unknown>>(formular);
    return entwurf && feld in entwurf ? (entwurf[feld] as T) : anfang;
  });

  function setzen(neu: T) {
    setWert(neu);
    const entwurf = entwurfLesen<Record<string, unknown>>(formular) ?? {};
    entwurfSchreiben(formular, { ...entwurf, [feld]: neu });
  }

  return [wert, setzen];
}
